import { Component, OnInit, OnChanges, DoCheck, Input, ViewChild, ChangeDetectorRef, SimpleChanges, KeyValueDiffers, KeyValueDiffer, IterableDiffers, IterableDiffer } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseChartDirective } from 'ng2-charts';
import {
  FilterState,
  ChartDataset,
  ChartColorConfig,
  InventoryProjectionRow,
  ForecastLineTable,
  ChartUpdateService,
  AnalyticsHttpService,
  AnalyticsExportService,
  getForecastBarChartOptions,
  getStackedBarChartOptions,
  getLineChartOptions,
  ANALYTICS_COLORS,
  CHART_UPDATE_DELAY,
  sortStackedBarChartData,
  limitBarChartData,
  SortDirection,
  DEFAULT_CHART_ITEM_LIMIT,
  calculateFirstDatasetTotal,
  getDatasetValue,
  getProductValue,
  getProductTotal,
  calculateForecastLineTotal,
  calculateInventoryProjectionCurrentTotal,
  calculateInventoryProjectionEndTotal,
  formatProductName,
  getBreakdownDisplayName as getBreakdownDisplayNameUtil,
  hexToRgb
} from 'app/core/analytics';

@Component({
  selector: 'kt-forecast-tab',
  templateUrl: './forecast-tab.component.html',
  styleUrls: [
    '../../analytics.component.scss',
    './forecast-tab.component.scss'
  ]
})
export class ForecastTabComponent implements OnInit, OnChanges, DoCheck {
  @Input() filters!: FilterState;
  @Input() forecastDays!: number;
  @Input() drinkOptions?: Array<{ value: string | number; label: string }>;
  @Input() productOptions?: Array<{ value: string | number; label: string }>;
  @Input() distributorOptions?: Array<{ value: string | number; label: string }>;
  @Input() marketOptions?: Array<{ value: string | number; label: string }>;
  @Input() clientOptions?: Array<{ value: string | number; label: string }>;
  @Input() campusOptions?: Array<{ value: string | number; label: string }>;
  @Input() machineOptions?: Array<{ value: string | number; label: string }>;

  @ViewChild('projectedConsumptionBarChart', { static: false }) projectedConsumptionBarChart!: BaseChartDirective;
  @ViewChild('inventoryProjectionLineChart', { static: false }) inventoryProjectionLineChart!: BaseChartDirective;
  @ViewChild('forecastLineChart', { static: false }) forecastLineChart!: BaseChartDirective;

  public forecastBarChartOptions = getForecastBarChartOptions(false);
  public stackedBarChartOptions = getStackedBarChartOptions();
  public lineChartOptions = getLineChartOptions();

  public barChartColors: ChartColorConfig[] = [];
  public lineChartColors: ChartColorConfig[] = [];

  public projectedConsumptionBarChartLabels: string[] = [];
  public projectedConsumptionBarChartData: ChartDataset[] = [];
  public projectedConsumptionBarChartSort: SortDirection = 'desc';
  public showAllForecastItems = false;

  private originalProjectedConsumptionBarChartLabels: string[] = [];
  private originalProjectedConsumptionBarChartData: ChartDataset[] = [];

  private fullProjectedConsumptionBarChartLabels: string[] = [];
  private fullProjectedConsumptionBarChartData: ChartDataset[] = [];

  public inventoryProjectionLabels: string[] = [];
  public inventoryProjectionData: ChartDataset[] = [];
  public inventoryProjectionTable: InventoryProjectionRow[] = [];

  public forecastLineLabels: string[] = [];
  public forecastLineData: ChartDataset[] = [];
  public forecastLineTable: ForecastLineTable = { labels: [], datasets: [] };

  public isLoadingForecastConsumption = false;
  public isLoadingForecastConsumptionOverTime = false;
  public isLoadingInventoryProjection = false;

  private filtersDiffer: KeyValueDiffer<string, any> | null = null;
  private arrayDiffers: Map<string, IterableDiffer<string>> = new Map();
  private forecastDaysDiffer: KeyValueDiffer<string, any> | null = null;

  // Mock contextual data mapping for tooltips
  private contextualDataMap: Map<string, { partner?: string; client?: string; campus?: string }> = new Map();

  // Custom tooltip state
  public tooltipVisible = false;
  public tooltipX = 0;
  public tooltipY = 0;
  public tooltipText = '';

  constructor(
    private analyticsHttpService: AnalyticsHttpService,
    private chartUpdateService: ChartUpdateService,
    private analyticsExportService: AnalyticsExportService,
    private cdr: ChangeDetectorRef,
    private keyValueDiffers: KeyValueDiffers,
    private iterableDiffers: IterableDiffers
  ) {
    this.initializeColors();
  }

  ngOnInit(): void {
    this.filtersDiffer = this.keyValueDiffers.find(this.filters).create();
    this.forecastDaysDiffer = this.keyValueDiffers.find({ forecastDays: this.forecastDays }).create();
    this.initializeArrayDiffers();
    this.loadAllData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filters'] && !changes['filters'].firstChange) {
      this.filtersDiffer = this.keyValueDiffers.find(this.filters).create();
      this.initializeArrayDiffers();
      this.loadAllData();
    }
    if (changes['forecastDays'] && !changes['forecastDays'].firstChange) {
      this.forecastDaysDiffer = this.keyValueDiffers.find({ forecastDays: this.forecastDays }).create();
      this.loadAllData();
    }
  }

  ngDoCheck(): void {
    if (this.filtersDiffer) {
      const changes = this.filtersDiffer.diff(this.filters);
      if (changes) {
        this.loadAllData();
        return;
      }
    }

    if (this.forecastDaysDiffer) {
      const changes = this.forecastDaysDiffer.diff({ forecastDays: this.forecastDays });
      if (changes) {
        this.loadAllData();
        return;
      }
    }

    const arrayKeys = ['drinks', 'distributors', 'markets', 'clients', 'campuses', 'machines', 'products'];
    for (const key of arrayKeys) {
      const differ = this.arrayDiffers.get(key);
      if (differ) {
        const changes = differ.diff(this.filters[key]);
        if (changes) {
          this.loadAllData();
          return;
        }
      }
    }
  }

  private initializeArrayDiffers(): void {
    const arrayKeys = ['drinks', 'distributors', 'markets', 'clients', 'campuses', 'machines', 'products'];
    for (const key of arrayKeys) {
      this.arrayDiffers.set(key, this.iterableDiffers.find(this.filters[key]).create());
    }
  }

  private initializeColors(): void {
    this.barChartColors = [{
      backgroundColor: ANALYTICS_COLORS.PRIMARY,
      borderColor: ANALYTICS_COLORS.PRIMARY
    }];

    this.lineChartColors = [{
      borderColor: ANALYTICS_COLORS.PRODUCT[0],
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      pointBackgroundColor: ANALYTICS_COLORS.PRODUCT[0],
      pointBorderColor: ANALYTICS_COLORS.PRODUCT[0]
    }];
  }

  private clearChartData(): void {
    this.projectedConsumptionBarChartLabels = [];
    this.projectedConsumptionBarChartData = [{
      label: 'Projected Consumption',
      data: [],
      borderWidth: 0
    }];
    this.originalProjectedConsumptionBarChartLabels = [];
    this.originalProjectedConsumptionBarChartData = [];
    this.fullProjectedConsumptionBarChartLabels = [];
    this.fullProjectedConsumptionBarChartData = [];
    this.inventoryProjectionLabels = [];
    this.inventoryProjectionData = [];
    this.inventoryProjectionTable = [];
    this.forecastLineLabels = [];
    this.forecastLineData = [];
    this.forecastLineTable = { labels: [], datasets: [] };
    this.cdr.detectChanges();
  }

  private loadAllData(): void {
    this.loadForecastConsumptionData();
    this.loadForecastConsumptionOverTimeData();
    this.loadInventoryProjectionData();
  }

  private loadData<T>(
    loadingFlagRef: () => boolean,
    setLoadingFlag: (value: boolean) => void,
    httpCall: (params: Record<string, unknown>) => Observable<T>,
    onSuccess: (response: T) => void,
    onError?: () => void,
    modifyParams?: (params: Record<string, unknown>) => void
  ): void {
    if (loadingFlagRef()) {
      return;
    }

    const queryParams = this.buildQueryParams();
    setLoadingFlag(true);

    if (modifyParams) {
      modifyParams(queryParams);
    }

    httpCall(queryParams).subscribe(
      (response: T) => {
        setLoadingFlag(false);
        onSuccess(response);
      },
      (error: unknown) => {
        setLoadingFlag(false);
        if (onError) {
          onError();
        }
      }
    );
  }

  private buildQueryParams(): Record<string, unknown> {
    const params: Record<string, unknown> = {
      breakdownBy: this.filters.breakdownBy,
      datePreset: this.filters.datePreset,
      forecastDays: this.forecastDays
    };

    if (this.filters.datePreset !== 'custom') {
      params.days = this.filters.days;
    } else {
      if (this.filters.startDate) {
        params.startDate = this.filters.startDate;
      }
      if (this.filters.endDate) {
        params.endDate = this.filters.endDate;
      }
    }

    if (this.filters.distributors.length > 0) {
      params.distributors = this.filters.distributors.join(',');
    }
    if (this.filters.markets.length > 0) {
      params.markets = this.filters.markets.join(',');
    }
    if (this.filters.clients.length > 0) {
      params.clients = this.filters.clients.join(',');
    }
    if (this.filters.campuses.length > 0) {
      params.campuses = this.filters.campuses.join(',');
    }
    if (this.filters.machines.length > 0) {
      params.machines = this.filters.machines.join(',');
    }
    if (this.filters.products.length > 0) {
      params.products = this.filters.products.join(',');
    }
    if (this.filters.drinks.length > 0) {
      params.drinks = this.filters.drinks.join(',');
    }

    return params;
  }

  loadForecastConsumptionData(): void {
    this.loadData(
      () => this.isLoadingForecastConsumption,
      (value) => { this.isLoadingForecastConsumption = value; },
      (params) => this.analyticsHttpService.getForecastConsumption(params),
      (response) => this.handleForecastConsumptionResponse(response),
      () => {
        this.projectedConsumptionBarChartLabels = [];
        this.projectedConsumptionBarChartData = [];
        this.forceUpdateForecastChart();
      }
    );
  }

  private parseApiResponse(response: unknown): Record<string, unknown> | null {
    if (!response || typeof response !== 'object') {
      return null;
    }

    const responseObj = response as Record<string, unknown>;
    const data = (responseObj.data && typeof responseObj.data === 'object' && !Array.isArray(responseObj.data))
      ? responseObj.data as Record<string, unknown>
      : responseObj;

    return data;
  }

  /**
   * Extract contextual data from API response
   */
  private extractContextualData(data: Record<string, unknown>): Record<string, { partner?: string; client?: string; campus?: string }> | undefined {
    const contextualData = data.contextualData;
    if (contextualData && typeof contextualData === 'object' && !Array.isArray(contextualData)) {
      return contextualData as Record<string, { partner?: string; client?: string; campus?: string }>;
    }
    return undefined;
  }

  private handleForecastConsumptionResponse(response: unknown): void {
    const data = this.parseApiResponse(response);
    if (!data) {
      this.projectedConsumptionBarChartLabels = [];
      this.projectedConsumptionBarChartData = [];
      this.forceUpdateForecastChart();
      return;
    }

    const isSimpleFormat = data.data && Array.isArray(data.data) && !data.datasets;

    if (isSimpleFormat) {
      this.handleSimpleForecastConsumptionResponse(data);
    } else {
      this.handleStackedForecastConsumptionResponse(data);
    }
  }

  private handleSimpleForecastConsumptionResponse(data: Record<string, unknown>): void {
    const labels = (data.labels as string[]) || [];
    const values = (data.data as number[]) || [];

    const formattedLabels = this.filters.breakdownBy === 'product'
      ? labels.map(label => formatProductName(label))
      : labels.slice();

    this.projectedConsumptionBarChartLabels = formattedLabels;

    // Populate contextual data from API response
    const contextualData = this.extractContextualData(data);
    if (contextualData) {
      this.populateContextualData(formattedLabels, contextualData);
    }

    this.projectedConsumptionBarChartData = [{
      label: 'Projected Consumption',
      data: values.slice(),
      backgroundColor: ANALYTICS_COLORS.PRIMARY,
      borderColor: ANALYTICS_COLORS.PRIMARY,
      borderWidth: 0
    }];

    this.originalProjectedConsumptionBarChartLabels = formattedLabels.slice();
    this.originalProjectedConsumptionBarChartData = this.projectedConsumptionBarChartData.map(dataset => ({
      ...dataset,
      data: (dataset.data as number[]).slice()
    }));

    this.sortProjectedConsumptionBarChart();

    this.barChartColors = [{
      backgroundColor: ANALYTICS_COLORS.PRIMARY,
      borderColor: ANALYTICS_COLORS.PRIMARY
    }];

    this.forceUpdateForecastChart();
  }

  private handleStackedForecastConsumptionResponse(data: Record<string, unknown>): void {
    const labels = (data.labels as string[]) || [];
    const datasets = (data.datasets as Array<Record<string, unknown>>) || [];

    this.projectedConsumptionBarChartLabels = labels.slice();

    // Populate contextual data from API response
    const contextualData = this.extractContextualData(data);
    if (contextualData) {
      this.populateContextualData(labels.slice(), contextualData);
    }

    this.projectedConsumptionBarChartData = datasets.map((dataset, index) => {
      const productData = (dataset.data as number[]) || [];
      const productLabel = (dataset.label as string) || `Product ${index + 1}`;
      const formattedProductLabel = formatProductName(productLabel);
      const colorIndex = index % ANALYTICS_COLORS.PRODUCT.length;
      const color = ANALYTICS_COLORS.PRODUCT[colorIndex];

      return {
        label: formattedProductLabel,
        data: productData.slice(),
        backgroundColor: color,
        borderColor: color,
        borderWidth: 0
      };
    });

    this.originalProjectedConsumptionBarChartLabels = labels.slice();
    this.originalProjectedConsumptionBarChartData = this.projectedConsumptionBarChartData.map(dataset => ({
      ...dataset,
      data: (dataset.data as number[]).slice()
    }));

    this.sortProjectedConsumptionBarChart();

    this.barChartColors = this.projectedConsumptionBarChartData.map(dataset => ({
      backgroundColor: typeof dataset.backgroundColor === 'string' ? dataset.backgroundColor : ANALYTICS_COLORS.PRIMARY,
      borderColor: typeof dataset.borderColor === 'string' ? dataset.borderColor : ANALYTICS_COLORS.PRIMARY
    }));

    this.forceUpdateForecastChart();
  }

  loadForecastConsumptionOverTimeData(): void {
    this.loadData(
      () => this.isLoadingForecastConsumptionOverTime,
      (value) => { this.isLoadingForecastConsumptionOverTime = value; },
      (params) => this.analyticsHttpService.getForecastConsumptionOverTime(params),
      (response) => this.handleForecastConsumptionOverTimeResponse(response),
      () => {
        this.forecastLineLabels = [];
        this.forecastLineData = [];
        this.forceUpdateForecastLineChart();
      },
      (params) => { delete params.breakdownBy; }
    );
  }

  private handleForecastConsumptionOverTimeResponse(response: unknown): void {
    const data = this.parseApiResponse(response);
    if (!data) {
      this.forecastLineLabels = [];
      this.forecastLineData = [];
      this.forceUpdateForecastLineChart();
      return;
    }

    const labels = (data.labels as string[]) || [];
    const datasets = (data.datasets as Array<Record<string, unknown>>) || [];

    this.forecastLineLabels = labels.slice();

    this.forecastLineData = datasets.map((dataset, index) => {
      const productData = (dataset.data as number[]) || [];
      const productLabel = (dataset.label as string) || `Product ${index + 1}`;
      const formattedProductLabel = formatProductName(productLabel);
      const colorIndex = index % ANALYTICS_COLORS.PRODUCT.length;
      const color = ANALYTICS_COLORS.PRODUCT[colorIndex];

      return {
        label: formattedProductLabel,
        data: productData.slice(),
        borderColor: color,
        backgroundColor: `rgba(${hexToRgb(color)}, 0.1)`,
        pointBackgroundColor: color,
        pointBorderColor: color,
        fill: false,
        borderWidth: 2,
        pointRadius: 2,
        pointHoverRadius: 4,
        lineTension: 0.4
      };
    });

    this.lineChartColors = this.forecastLineData.map(dataset => ({
      borderColor: typeof dataset.borderColor === 'string' ? dataset.borderColor : ANALYTICS_COLORS.PRODUCT[0],
      backgroundColor: typeof dataset.backgroundColor === 'string' ? dataset.backgroundColor : 'rgba(59, 130, 246, 0.1)',
      pointBackgroundColor: typeof dataset.pointBackgroundColor === 'string' ? dataset.pointBackgroundColor : ANALYTICS_COLORS.PRODUCT[0],
      pointBorderColor: typeof dataset.pointBorderColor === 'string' ? dataset.pointBorderColor : ANALYTICS_COLORS.PRODUCT[0]
    }));

    this.forecastLineTable = {
      labels: labels.slice(),
      datasets: this.forecastLineData.map(ds => ({ ...ds }))
    };

    this.forceUpdateForecastLineChart();
  }

  private forceUpdateForecastLineChart(): void {
    const tempLabels = this.forecastLineLabels.slice();
    const tempDatasets = this.forecastLineData.map(ds => ({ ...ds }));

    this.forecastLineLabels = [];
    this.forecastLineData = [];
    this.cdr.detectChanges();

    setTimeout(() => {
      this.forecastLineLabels = tempLabels;
      this.forecastLineData = tempDatasets;
      this.cdr.detectChanges();
    }, 10);
  }

  loadInventoryProjectionData(): void {
    this.loadData(
      () => this.isLoadingInventoryProjection,
      (value) => { this.isLoadingInventoryProjection = value; },
      (params) => this.analyticsHttpService.getInventoryProjection(params),
      (response) => this.handleInventoryProjectionResponse(response),
      () => {
        this.inventoryProjectionLabels = [];
        this.inventoryProjectionData = [];
        this.inventoryProjectionTable = [];
        this.forceUpdateInventoryProjectionChart();
      },
      (params) => { delete params.breakdownBy; }
    );
  }

  private handleInventoryProjectionResponse(response: unknown): void {
    const data = this.parseApiResponse(response);
    if (!data) {
      this.inventoryProjectionLabels = [];
      this.inventoryProjectionData = [];
      this.inventoryProjectionTable = [];
      this.forceUpdateInventoryProjectionChart();
      return;
    }

    const labels = (data.labels as string[]) || [];
    const datasets = (data.datasets as Array<Record<string, unknown>>) || [];
    const summary = (data.summary as Array<Record<string, unknown>>) || [];

    this.inventoryProjectionLabels = labels.slice();

    this.inventoryProjectionData = datasets.map((dataset, index) => {
      const productData = (dataset.data as number[]) || [];
      const productLabel = (dataset.label as string) || `Product ${index + 1}`;
      const formattedProductLabel = formatProductName(productLabel);
      const colorIndex = index % ANALYTICS_COLORS.PRODUCT.length;
      const color = ANALYTICS_COLORS.PRODUCT[colorIndex];

      return {
        label: formattedProductLabel,
        data: productData.slice(),
        borderColor: color,
        backgroundColor: `rgba(${hexToRgb(color)}, 0.1)`,
        pointBackgroundColor: color,
        pointBorderColor: color,
        fill: false,
        borderWidth: 2,
        pointRadius: 2,
        pointHoverRadius: 4,
        lineTension: 0.4
      };
    });

    this.lineChartColors = this.inventoryProjectionData.map(dataset => ({
      borderColor: typeof dataset.borderColor === 'string' ? dataset.borderColor : ANALYTICS_COLORS.PRODUCT[0],
      backgroundColor: typeof dataset.backgroundColor === 'string' ? dataset.backgroundColor : 'rgba(59, 130, 246, 0.1)',
      pointBackgroundColor: typeof dataset.pointBackgroundColor === 'string' ? dataset.pointBackgroundColor : ANALYTICS_COLORS.PRODUCT[0],
      pointBorderColor: typeof dataset.pointBorderColor === 'string' ? dataset.pointBorderColor : ANALYTICS_COLORS.PRODUCT[0]
    }));

    this.inventoryProjectionTable = summary.map(item => ({
      product: formatProductName((item.product as string) || ''),
      current: Math.round(((item.current as number) || 0) * 100) / 100,
      projectedEnd: Math.round(((item.projectedEnd as number) || 0) * 100) / 100
    }));

    this.forceUpdateInventoryProjectionChart();
  }

  private forceUpdateInventoryProjectionChart(): void {
    const tempLabels = this.inventoryProjectionLabels.slice();
    const tempDatasets = this.inventoryProjectionData.map(ds => ({ ...ds }));

    this.inventoryProjectionLabels = [];
    this.inventoryProjectionData = [];
    this.cdr.detectChanges();

    setTimeout(() => {
      this.inventoryProjectionLabels = tempLabels;
      this.inventoryProjectionData = tempDatasets;
      this.cdr.detectChanges();
    }, 10);
  }

  toggleProjectedConsumptionBarChartSort(): void {
    this.projectedConsumptionBarChartSort = this.projectedConsumptionBarChartSort === 'desc' ? 'asc' : 'desc';
    this.sortProjectedConsumptionBarChart();
    this.forceUpdateForecastChart();
  }

  private sortProjectedConsumptionBarChart(): void {
    if (!this.projectedConsumptionBarChartLabels || !this.projectedConsumptionBarChartData || this.projectedConsumptionBarChartData.length === 0) {
      return;
    }

    const labelsToSort = this.originalProjectedConsumptionBarChartLabels.length > 0
      ? this.originalProjectedConsumptionBarChartLabels
      : this.projectedConsumptionBarChartLabels;
    const dataToSort = this.originalProjectedConsumptionBarChartData.length > 0
      ? this.originalProjectedConsumptionBarChartData
      : this.projectedConsumptionBarChartData;

    const result = sortStackedBarChartData(
      labelsToSort,
      dataToSort,
      this.projectedConsumptionBarChartSort,
      false
    );
    this.fullProjectedConsumptionBarChartLabels = result.labels;
    this.fullProjectedConsumptionBarChartData = result.datasets;

    this.updateForecastDisplayData();
  }

  private updateForecastDisplayData(): void {
    const limit = this.showAllForecastItems
      ? this.fullProjectedConsumptionBarChartLabels.length
      : DEFAULT_CHART_ITEM_LIMIT;

    const result = limitBarChartData(
      this.fullProjectedConsumptionBarChartLabels,
      this.fullProjectedConsumptionBarChartData,
      undefined,
      limit,
      false
    );

    this.projectedConsumptionBarChartLabels = result.labels;
    this.projectedConsumptionBarChartData = result.datasets;
  }

  toggleShowAllForecastItems(): void {
    this.showAllForecastItems = !this.showAllForecastItems;
    this.updateForecastDisplayData();
    this.forceUpdateForecastChart();
  }
  private forceUpdateForecastChart(): void {
    this.chartUpdateService.recreateBarChart(
      this.cdr,
      () => ({
        labels: this.projectedConsumptionBarChartLabels,
        datasets: this.projectedConsumptionBarChartData,
        compareData: [] // Forecast doesn't have compare data
      }),
      (data) => {
        this.projectedConsumptionBarChartLabels = data.labels;
        this.projectedConsumptionBarChartData = data.datasets;
      },
      'ForecastBarChart'
    );
  }

  shouldShowForecastToggle(): boolean {
    return this.fullProjectedConsumptionBarChartLabels.length > DEFAULT_CHART_ITEM_LIMIT;
  }

  getForecastToggleLabel(): string {
    if (this.showAllForecastItems) {
      return this.projectedConsumptionBarChartSort === 'desc' ? 'Top 10' : 'Bottom 10';
    }
    return 'All';
  }

  getForecastToggleTitle(): string {
    if (this.showAllForecastItems) {
      return this.projectedConsumptionBarChartSort === 'desc' ? 'Show top 10' : 'Show bottom 10';
    }
    return 'Show all';
  }
  getForecastBarChartOptions(stacked: boolean): unknown {
    return stacked ? this.stackedBarChartOptions : this.forecastBarChartOptions;
  }

  isForecastStacked(): boolean {
    return this.filters.breakdownBy !== 'product' && this.projectedConsumptionBarChartData.length > 1;
  }

  getForecastValueForProduct(productIndex: number, labelIndex: number): number {
    return getProductValue(this.projectedConsumptionBarChartData, productIndex, labelIndex);
  }

  getForecastValue(index: number): number {
    if (!this.projectedConsumptionBarChartData || this.projectedConsumptionBarChartData.length === 0) {
      return 0;
    }
    return getDatasetValue(this.projectedConsumptionBarChartData[0], index);
  }

  getForecastTotalForProduct(productIndex: number): number {
    return getProductTotal(this.projectedConsumptionBarChartData, productIndex);
  }

  getForecastBarChartTotal(): number {
    return calculateFirstDatasetTotal(this.projectedConsumptionBarChartData);
  }

  getForecastLineTotal(ds: ChartDataset): number {
    return calculateForecastLineTotal(ds);
  }

  getInventoryProjectionCurrentTotal(): number {
    return calculateInventoryProjectionCurrentTotal(this.inventoryProjectionTable);
  }

  getInventoryProjectionEndTotal(): number {
    return calculateInventoryProjectionEndTotal(this.inventoryProjectionTable);
  }

  getBreakdownDisplayName(option: string): string {
    return getBreakdownDisplayNameUtil(option);
  }

  hasInsufficientTimeseriesData(labels: string[]): boolean {
    return labels.length === 1;
  }

  hasNoData(labels: string[], datasets: ChartDataset[]): boolean {
    return labels.length === 0 || datasets.length === 0 || (datasets.length > 0 && datasets[0].data.length === 0);
  }

  /**
   * Get tooltip text for a label based on breakdown type
   */
  getContextualTooltip(label: string): string {
    const breakdownBy = this.filters.breakdownBy;
    const context = this.contextualDataMap.get(label);

    if (!context) {
      return '';
    }

    if (breakdownBy === 'client') {
      // Show Partner
      return context.partner ? `Partner: ${context.partner}` : '';
    } else if (breakdownBy === 'campus') {
      // Show Partner first, then Client
      const parts: string[] = [];
      if (context.partner) {
        parts.push(`Partner: ${context.partner}`);
      }
      if (context.client) {
        parts.push(`Client: ${context.client}`);
      }
      return parts.join('\n');
    } else if (breakdownBy === 'machine') {
      // Show Partner first, then Client, then Campus
      const parts: string[] = [];
      if (context.partner) {
        parts.push(`Partner: ${context.partner}`);
      }
      if (context.client) {
        parts.push(`Client: ${context.client}`);
      }
      if (context.campus) {
        parts.push(`Campus: ${context.campus}`);
      }
      return parts.join('\n');
    }

    return '';
  }

  /**
   * Check if tooltip should be shown based on breakdown type
   */
  shouldShowContextualTooltip(): boolean {
    const breakdownBy = this.filters.breakdownBy;
    return breakdownBy === 'client' || breakdownBy === 'campus' || breakdownBy === 'machine';
  }

  /**
   * Show custom tooltip on mouse enter
   */
  onTooltipMouseEnter(event: MouseEvent, label: string): void {
    if (!this.shouldShowContextualTooltip()) {
      return;
    }
    const tooltipText = this.getContextualTooltip(label);
    if (!tooltipText) {
      return;
    }
    this.tooltipVisible = true;
    this.tooltipText = tooltipText;
    // Set position once on enter - tooltip stays static
    this.tooltipX = event.clientX;
    this.tooltipY = event.clientY - 10; // 10px above cursor
  }

  /**
   * Hide tooltip on mouse leave
   */
  onTooltipMouseLeave(): void {
    this.tooltipVisible = false;
  }

  /**
   * Populate contextual data from API response
   */
  private populateContextualData(labels: string[], contextualData: Record<string, { partner?: string; client?: string; campus?: string }>): void {
    const breakdownBy = this.filters.breakdownBy;

    // Only populate for client, campus, or machine breakdowns
    if (breakdownBy !== 'client' && breakdownBy !== 'campus' && breakdownBy !== 'machine') {
      return;
    }

    // Clear existing contextual data for these labels
    labels.forEach(label => {
      this.contextualDataMap.delete(label);
    });

    // Populate from API response
    labels.forEach((label) => {
      const contextData = contextualData[label];
      if (contextData) {
        const context: { partner?: string; client?: string; campus?: string } = {};

        if (contextData.partner) {
          context.partner = contextData.partner;
        }
        if (contextData.client) {
          context.client = contextData.client;
        }
        if (contextData.campus) {
          context.campus = contextData.campus;
        }

        // Only set if we have at least one property
        if (context.partner || context.client || context.campus) {
          this.contextualDataMap.set(label, context);
        }
      }
    });
  }

  /**
   * Check if data is available for export
   * Check all data sources: projected consumption bar chart, inventory projection table, and time series data
   */
  hasData(): boolean {
    // Check projected consumption bar chart (limited arrays)
    const hasLimitedBarChartData = this.projectedConsumptionBarChartLabels.length > 0 &&
           this.projectedConsumptionBarChartData.length > 0 &&
           (this.projectedConsumptionBarChartData[0] && this.projectedConsumptionBarChartData[0].data && this.projectedConsumptionBarChartData[0].data.length > 0);

    // Check projected consumption bar chart (full arrays)
    const hasFullBarChartData = this.fullProjectedConsumptionBarChartLabels.length > 0 &&
           this.fullProjectedConsumptionBarChartData.length > 0 &&
           (this.fullProjectedConsumptionBarChartData[0] && this.fullProjectedConsumptionBarChartData[0].data && this.fullProjectedConsumptionBarChartData[0].data.length > 0);

    // Check inventory projection table
    const hasInventoryData = this.inventoryProjectionTable && this.inventoryProjectionTable.length > 0;

    // Check time series data
    const hasTimeSeriesData = this.forecastLineLabels.length > 0 &&
           this.forecastLineData.length > 0 &&
           (this.forecastLineData[0] && this.forecastLineData[0].data && this.forecastLineData[0].data.length > 0);

    // Return true if any data source has data
    return hasLimitedBarChartData || hasFullBarChartData || hasInventoryData || hasTimeSeriesData;
  }

  /**
   * Export forecast tables to Excel (all 3 tables as separate sheets)
   */
  exportTable(): void {
    try {
      // Use full arrays for export (all data, not just visible)
      const projectedLabels = this.fullProjectedConsumptionBarChartLabels.length > 0
        ? this.fullProjectedConsumptionBarChartLabels
        : this.projectedConsumptionBarChartLabels;

      // Safely extract data, handling single-row cases
      let projectedData: ChartDataset[] = [];
      if (this.fullProjectedConsumptionBarChartData.length > 0) {
        projectedData = this.fullProjectedConsumptionBarChartData;
      } else if (this.projectedConsumptionBarChartData.length > 0) {
        projectedData = this.projectedConsumptionBarChartData;
      }

      // Ensure we have data before proceeding
      if (projectedLabels.length === 0 || projectedData.length === 0) {
        console.warn('No projected consumption data available for export');
        // Still allow export if inventory projection or time series data exists
        if ((!this.inventoryProjectionTable || this.inventoryProjectionTable.length === 0) &&
            (!this.forecastLineData || this.forecastLineData.length === 0)) {
          console.warn('No forecast data available for export');
          return;
        }
      }

      // Calculate totals for time series data
      const timeSeriesTotals: number[] = [];
      if (this.forecastLineData && this.forecastLineData.length > 0) {
        this.forecastLineData.forEach(dataset => {
          timeSeriesTotals.push(this.getForecastLineTotal(dataset));
        });
      }

      const exportData = {
        projectedConsumption: {
          labels: projectedLabels,
          data: projectedData,
          isStacked: this.isForecastStacked(),
          breakdownBy: this.filters.breakdownBy,
          total: this.getForecastBarChartTotal()
        },
        inventoryProjection: {
          table: this.inventoryProjectionTable || [],
          currentTotal: this.getInventoryProjectionCurrentTotal(),
          endTotal: this.getInventoryProjectionEndTotal()
        },
        projectedConsumptionOverTime: {
          labels: this.forecastLineLabels || [],
          datasets: this.forecastLineData || [],
          totals: timeSeriesTotals
        }
      };

      this.analyticsExportService.exportForecastTable(
        exportData,
        this.filters,
        this.forecastDays,
        this.drinkOptions,
        this.productOptions,
        this.distributorOptions,
        this.marketOptions,
        this.clientOptions,
        this.campusOptions,
        this.machineOptions
      );
    } catch (error) {
      console.error('Error exporting forecast tables:', error);
      throw error;
    }
  }
}

